(() => {
  const initializedAttribute = 'data-multicolumn-autoplay-initialized';

  function initializeAutoplay(root = document) {
    root.querySelectorAll('.multicolumn slider-component [data-autoplay="true"], .multicolumn slider-component [data-continuous="true"]').forEach((slider) => {
      if (slider.hasAttribute(initializedAttribute)) return;

      const slides = Array.from(slider.querySelectorAll('.slider__slide'));
      const sliderComponent = slider.closest('slider-component');
      const autoplayButton = sliderComponent?.querySelector('.slideshow__autoplay');
      const speed = Number(slider.dataset.speed);
      const isContinuous = slider.dataset.continuous === 'true';
      const interactionTarget = isContinuous ? sliderComponent || slider : slider;
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (slides.length < 2 || !speed) return;

      slider.setAttribute(initializedAttribute, '');

      let isPlaying = !(isContinuous && reducedMotionQuery.matches);
      let isHovering = false;
      let isFocused = false;
      let isTouching = false;
      let timer;
      let animationFrame;
      let lastTimestamp;
      let loopWidth = 0;
      let animationPosition = 0;
      let loopResizeObserver;

      const updateButton = () => {
        if (!autoplayButton) return;
        autoplayButton.classList.toggle('slideshow__autoplay--paused', !isPlaying);
        autoplayButton.setAttribute(
          'aria-label',
          isPlaying ? window.accessibilityStrings.pauseSlideshow : window.accessibilityStrings.playSlideshow
        );
      };

      const canAnimate = () => isPlaying && !document.hidden && !isHovering && !isFocused && !isTouching && !(isContinuous && reducedMotionQuery.matches);

      const cancelAnimation = () => {
        if (!animationFrame) return;
        cancelAnimationFrame(animationFrame);
        animationFrame = undefined;
        lastTimestamp = undefined;
      };

      const normalizeLoopPosition = () => {
        if (!loopWidth) return;
        while (slider.scrollLeft >= loopWidth) slider.scrollLeft -= loopWidth;
      };

      const getSlideOffset = () => sliderComponent?.sliderItemOffset || slides[1].offsetLeft - slides[0].offsetLeft;

      const updateLoopGeometry = () => {
        const firstClone = slider.querySelector('.slider__slide[aria-hidden="true"]');
        const measuredLoopWidth = firstClone?.offsetLeft - slides[0].offsetLeft;
        if (!measuredLoopWidth || measuredLoopWidth <= 0) return;
        loopWidth = measuredLoopWidth;
        normalizeLoopPosition();
      };

      const animate = (timestamp) => {
        animationFrame = undefined;
        if (!canAnimate()) return;

        if (lastTimestamp !== undefined) {
          const slideOffset = getSlideOffset();
          if (!slideOffset || (isContinuous && !loopWidth)) {
            lastTimestamp = timestamp;
            animationFrame = requestAnimationFrame(animate);
            return;
          }
          const pixelsPerSecond = slideOffset / speed;
          animationPosition += (timestamp - lastTimestamp) * pixelsPerSecond / 1000;
          while (animationPosition >= loopWidth) animationPosition -= loopWidth;
          slider.scrollLeft = animationPosition;
        }

        lastTimestamp = timestamp;
        animationFrame = requestAnimationFrame(animate);
      };

      const startAnimation = () => {
        if (!isContinuous || !canAnimate() || animationFrame) return;
        animationPosition = slider.scrollLeft;
        lastTimestamp = undefined;
        animationFrame = requestAnimationFrame(animate);
      };

      const stopAnimation = () => {
        if (isContinuous) cancelAnimation();
      };

      const schedule = () => {
        clearTimeout(timer);
        if (isContinuous || !isPlaying || document.hidden) return;
        timer = window.setTimeout(advance, speed * 1000);
      };

      const advance = () => {
        const slideOffset = sliderComponent?.sliderItemOffset || slides[1].offsetLeft - slides[0].offsetLeft;
        const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
        const currentPage = sliderComponent?.currentPage || 1;
        const totalPages = sliderComponent?.totalPages || 1;
        const nextPage = currentPage >= totalPages ? 1 : currentPage + 1;
        const nextScroll = nextPage === totalPages ? maxScroll : (nextPage - 1) * slideOffset;

        slider.scrollTo({
          left: nextPage === 1 ? 0 : nextScroll,
          behavior: 'smooth',
        });
        schedule();
      };

      if (isContinuous) {
        slides.forEach((slide) => {
          const clone = slide.cloneNode(true);
          clone.removeAttribute('id');
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
          clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
            element.setAttribute('tabindex', '-1');
          });
          slider.appendChild(clone);
        });

        loopResizeObserver = new ResizeObserver(updateLoopGeometry);
        slider.querySelectorAll('.slider__slide').forEach((slide) => loopResizeObserver.observe(slide));
        updateLoopGeometry();
        slider.addEventListener('scroll', normalizeLoopPosition, { passive: true });
        window.requestAnimationFrame(() => {
          updateLoopGeometry();
          startAnimation();
        });
      } else {
        schedule();
      }

      autoplayButton?.addEventListener('click', () => {
        if (isContinuous && reducedMotionQuery.matches) return;
        isFocused = false;
        isPlaying = !isPlaying;
        updateButton();
        if (isPlaying) {
          startAnimation();
          schedule();
        } else {
          stopAnimation();
          clearTimeout(timer);
        }
      });

      const isMouseLikePointer = (event) => event.pointerType === 'mouse' || event.pointerType === 'pen';

      interactionTarget.addEventListener('pointerenter', (event) => {
        if (!isMouseLikePointer(event)) return;
        isHovering = true;
        stopAnimation();
        if (!isContinuous && isPlaying) clearTimeout(timer);
      });
      interactionTarget.addEventListener('pointerleave', (event) => {
        if (!isMouseLikePointer(event)) return;
        isHovering = false;
        startAnimation();
        schedule();
      });
      interactionTarget.addEventListener('focusin', () => {
        if (document.activeElement === autoplayButton) return;
        isFocused = true;
        stopAnimation();
        if (!isContinuous && isPlaying) clearTimeout(timer);
      });
      interactionTarget.addEventListener('focusout', () => {
        isFocused = false;
        startAnimation();
        schedule();
      });
      slider.addEventListener('touchstart', () => {
        isTouching = true;
        stopAnimation();
        clearTimeout(timer);
      }, { passive: true });
      const endTouch = () => {
        isTouching = false;
        startAnimation();
        schedule();
      };
      slider.addEventListener('touchend', endTouch, { passive: true });
      slider.addEventListener('touchcancel', endTouch, { passive: true });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          stopAnimation();
          clearTimeout(timer);
        } else {
          startAnimation();
          schedule();
        }
      });

      updateButton();
    });
  }

  document.addEventListener('DOMContentLoaded', () => initializeAutoplay());
  document.addEventListener('shopify:section:load', (event) => initializeAutoplay(event.target));
})();

(function () {
  const initializedAttribute = 'data-hover-card-initialized';
  const cardSelector = '[data-hover-card]';

  function initializeHoverCards(root = document) {
    root.querySelectorAll(cardSelector).forEach((card) => {
      if (card.hasAttribute(initializedAttribute)) return;
      if (card.closest('[aria-hidden="true"]')) return;

      const front = card.querySelector('.multicolumn-hover-card__front');
      const reveal = card.querySelector('.multicolumn-hover-card__reveal');
      if (!front || !reveal) return;

      card.setAttribute(initializedAttribute, '');

      const setActive = (active) => {
        card.classList.toggle('is-active', active);
        front.setAttribute('aria-expanded', String(active));
        reveal.setAttribute('aria-hidden', String(!active));
      };

      const isFinePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      let keepClosedOnFocus = false;

      card.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse' || event.pointerType === 'pen') setActive(true);
      });

      card.addEventListener('pointerleave', (event) => {
        if ((event.pointerType === 'mouse' || event.pointerType === 'pen') && !reveal.contains(document.activeElement)) {
          setActive(false);
        }
      });

      front.addEventListener('click', (event) => {
        if (event.detail === 0 || !isFinePointer()) {
          setActive(!card.classList.contains('is-active'));
        } else {
          setActive(true);
        }
      });

      card.addEventListener('focusin', () => {
        if (!keepClosedOnFocus) setActive(true);
      });
      card.addEventListener('focusout', (event) => {
        if (!card.contains(event.relatedTarget)) setActive(false);
      });

      card.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          keepClosedOnFocus = true;
          setActive(false);
          front.focus();
          window.setTimeout(() => {
            keepClosedOnFocus = false;
          }, 0);
        }
      });

      setActive(false);
    });
  }

  document.addEventListener('pointerdown', (event) => {
    document.querySelectorAll(`${cardSelector}.is-active`).forEach((card) => {
      if (!card.contains(event.target)) {
        const front = card.querySelector('.multicolumn-hover-card__front');
        const reveal = card.querySelector('.multicolumn-hover-card__reveal');
        card.classList.remove('is-active');
        front?.setAttribute('aria-expanded', 'false');
        reveal?.setAttribute('aria-hidden', 'true');
      }
    });
  });

  document.addEventListener('DOMContentLoaded', () => initializeHoverCards());
  document.addEventListener('shopify:section:load', (event) => initializeHoverCards(event.target));
})();
